import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Chip,
    Divider,
    Button,
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as CopyIcon,
    Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';

/**
 * SocialShareModal – reusable share dialog for webapp.
 *
 * Props:
 *  open        – controls visibility
 *  onClose     – callback when modal is closed
 *  type        – 'ride' | 'request'  (changes text / URLs)
 *  data        – object with originCity, destinationCity, departureDateTime, etc.
 *  showSuccess – if true, shows a "🎉 Posted!" celebration header (for post-create flow)
 */

interface SocialShareModalProps {
    open: boolean;
    onClose: () => void;
    type?: 'ride' | 'request';
    data?: any;
    showSuccess?: boolean;
}

interface PlatformButton {
    name: string;
    emoji: string;
    color: string;
    build: (text: string, url: string) => string;
}

const PLATFORMS: PlatformButton[] = [
    {
        name: 'WhatsApp',
        emoji: '🟢',
        color: '#25D366',
        build: (text, url) =>
            `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
    },
    {
        name: 'Telegram',
        emoji: '✈️',
        color: '#0088cc',
        build: (text, url) =>
            `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
    },
    {
        name: 'Facebook',
        emoji: '📘',
        color: '#1877F2',
        build: (_text, url) =>
            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
    {
        name: 'Twitter / X',
        emoji: '🐦',
        color: '#000000',
        build: (text, url) =>
            `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
    },
    {
        name: 'LinkedIn',
        emoji: '💼',
        color: '#0A66C2',
        build: (text, url) =>
            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`,
    },
    {
        name: 'Email',
        emoji: '📧',
        color: '#EA4335',
        build: (text, url) =>
            `mailto:?subject=${encodeURIComponent('Check out this ride on Ride Club!')}&body=${encodeURIComponent(`${text}\n\n${url}`)}`,
    },
];

const SocialShareModal: React.FC<SocialShareModalProps> = ({
    open,
    onClose,
    type = 'ride',
    data = {},
    showSuccess = false,
}) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [copied, setCopied] = useState(false);
    const [snackbar, setSnackbar] = useState('');

    const isRide = type === 'ride';
    const id = data?.id || data?.ride?.id || data?.rideId;
    const baseUrl = 'https://rideclubnet.com';
    const path = isRide
        ? (id ? `/ride/${id}` : '/browse-rides')
        : (id ? `/browse-requests?requestId=${id}` : '/browse-requests');
    const referralQuery = (user as any)?.referralCode ? `ref=${(user as any).referralCode}` : '';
    let shareUrl = `${baseUrl}${path}`;
    if (referralQuery) {
        shareUrl += shareUrl.includes('?') ? `&${referralQuery}` : `?${referralQuery}`;
    }

    const origin = data?.originCity?.name || data?.originName || '—';
    const destination = data?.destinationCity?.name || data?.destName || '—';

    let shareText = isRide
        ? t('share.rideMessage', { origin, destination, defaultValue: `🚗 I just posted a ride from ${origin} to ${destination}! Join me on Ride Club.` })
        : t('share.requestMessage', { origin, destination, defaultValue: `✋ I'm looking for a ride from ${origin} to ${destination}! If you or anyone you know is driving, sign up on Ride Club to offer this ride! 🚗` });

    if ((user as any)?.referralCode) {
        shareText += `\n\n🎁 ${t('share.referralBonus', 'Use my referral link and get 3 free days on Ride Club!')}`;
    }

    const handlePlatformClick = (platform: PlatformButton) => {
        const platformUrl = platform.build(shareText, shareUrl);
        if (platform.name === 'Email') {
            window.location.href = platformUrl;
        } else {
            window.open(platformUrl, '_blank', 'noopener,noreferrer,width=600,height=500');
        }
        setSnackbar(`Opening ${platform.name}...`);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
            setCopied(true);
            setSnackbar('Link copied to clipboard!');
            setTimeout(() => setCopied(false), 2500);
        } catch {
            setSnackbar('Could not copy. Please copy the link manually.');
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: isRide ? 'Ride Club – New Ride' : 'Ride Club – Ride Request',
                    text: shareText,
                    url: shareUrl,
                });
            } catch {
                // user cancelled
            }
        }
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                    },
                }}
            >
                {/* Gradient Header */}
                <Box
                    sx={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                        py: showSuccess ? 4 : 2.5,
                        px: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        position: 'relative',
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{ position: 'absolute', top: 10, right: 10, color: 'rgba(255,255,255,0.8)' }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {showSuccess && (
                        <Typography sx={{ fontSize: 48, mb: 1, animation: 'pulse 1s infinite' }}>
                            🎉
                        </Typography>
                    )}

                    <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800, textAlign: 'center' }}>
                        {showSuccess
                            ? (isRide ? t('share.ridePostedTitle', 'Ride Posted! 🚗') : t('share.requestPostedTitle', 'Request Sent! ✋'))
                            : t('share.shareTitle', 'Share this Ride')}
                    </Typography>

                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5, textAlign: 'center', fontSize: 14 }}>
                        {origin} → {destination}
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    {/* Referral Bonus Badge */}
                    {(user as any)?.referralCode && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                p: 2,
                                mb: 3,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                border: '1px solid #f59e0b',
                            }}
                        >
                            <Typography sx={{ fontSize: 28 }}>🎁</Typography>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>
                                    Share & earn 5 free days for each friend who joins!
                                </Typography>
                                <Typography sx={{ color: '#b45309', fontSize: 12 }}>
                                    Your referral code: <strong>{(user as any).referralCode}</strong>
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Platform Buttons */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 600 }}>
                        {t('share.chooseplatform', 'Share on')}
                    </Typography>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 3 }}>
                        {PLATFORMS.map((platform) => (
                            <Button
                                key={platform.name}
                                variant="outlined"
                                onClick={() => handlePlatformClick(platform)}
                                startIcon={<span style={{ fontSize: 18 }}>{platform.emoji}</span>}
                                sx={{
                                    borderColor: platform.color,
                                    color: platform.color,
                                    borderRadius: 3,
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    px: 2,
                                    py: 1,
                                    '&:hover': {
                                        backgroundColor: platform.color + '14',
                                        borderColor: platform.color,
                                    },
                                }}
                            >
                                {platform.name}
                            </Button>
                        ))}
                    </Box>

                    <Divider sx={{ mb: 2 }}>
                        <Chip label="or" size="small" />
                    </Divider>

                    {/* Copy Link Row */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            bgcolor: 'grey.50',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'text.secondary',
                                fontSize: 12,
                            }}
                        >
                            {shareUrl}
                        </Typography>
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                            <IconButton size="small" onClick={handleCopy} color={copied ? 'success' : 'default'}>
                                {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    {/* Native Share button (mobile browsers) */}
                    {typeof navigator !== 'undefined' && (navigator as any).share && (
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleNativeShare}
                            sx={{
                                mt: 2,
                                borderRadius: 3,
                                background: 'linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)',
                                fontWeight: 700,
                                textTransform: 'none',
                                fontSize: 15,
                                py: 1.5,
                            }}
                        >
                            📤 {t('share.shareNow', 'Share Now')}
                        </Button>
                    )}

                    {/* Continue without sharing button */}
                    <Button
                        fullWidth
                        variant="text"
                        onClick={onClose}
                        sx={{
                            mt: 1,
                            borderRadius: 3,
                            color: 'text.secondary',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: 14,
                            py: 1,
                            '&:hover': {
                                bgcolor: 'rgba(0,0,0,0.04)',
                                color: 'primary.main',
                            },
                        }}
                    >
                        {t('share.continueWithoutSharing', 'Continue without sharing')}
                    </Button>
                </DialogContent>
            </Dialog>

            <Snackbar
                open={!!snackbar}
                autoHideDuration={2500}
                onClose={() => setSnackbar('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar('')} severity="success" variant="filled">
                    {snackbar}
                </Alert>
            </Snackbar>
        </>
    );
};

export default SocialShareModal;
